import abTranslate from 'ab-translate/dist/src/abTranslate-runtime';
import debounce from 'just-debounce';
import { always, any, mapObjIndexed, prop } from 'ramda';
import React, { FC, Fragment } from 'react';
import { IntlConfig, IntlProvider as ReactIntlProvider } from 'react-intl';
import { useAsync } from 'react-use';
import { compose, withProps } from 'recompose';
import { FullHeightLoader } from '../../components/common';
import { BI_ERRORS, errorLogger } from '../../services/loggers/errorLogger';
import { getCorrectLocale, getLocale } from '../../utils/env/getLocale';
import { withExperiments } from '../../utils/hoc/withExperiments';
import { useMMGRSettings } from '../../utils/hoc/withMMGRSettings';
import styles from './LocaleProvider.scss';

const getDefault = prop('default');
const returnEmpty = always({});

const locale = getLocale();
const correctLocale = getCorrectLocale();

const pluralRulesDataByCode = {
  cs: () => import(/* webpackChunkName: 'locale-data-cs' */ '@formatjs/intl-pluralrules/dist/locale-data/cs'),
  da: () => import(/* webpackChunkName: 'locale-data-da' */ '@formatjs/intl-pluralrules/dist/locale-data/da'),
  de: () => import(/* webpackChunkName: 'locale-data-de' */ '@formatjs/intl-pluralrules/dist/locale-data/de'),
  en: () => import(/* webpackChunkName: 'locale-data-en' */ '@formatjs/intl-pluralrules/dist/locale-data/en'),
  es: () => import(/* webpackChunkName: 'locale-data-es' */ '@formatjs/intl-pluralrules/dist/locale-data/es'),
  fi: () => import(/* webpackChunkName: 'locale-data-fi' */ '@formatjs/intl-pluralrules/dist/locale-data/fi'),
  fr: () => import(/* webpackChunkName: 'locale-data-fr' */ '@formatjs/intl-pluralrules/dist/locale-data/fr'),
  hi: () => import(/* webpackChunkName: 'locale-data-hi' */ '@formatjs/intl-pluralrules/dist/locale-data/hi'),
  id: () => import(/* webpackChunkName: 'locale-data-id' */ '@formatjs/intl-pluralrules/dist/locale-data/id'),
  it: () => import(/* webpackChunkName: 'locale-data-it' */ '@formatjs/intl-pluralrules/dist/locale-data/it'),
  ja: () => import(/* webpackChunkName: 'locale-data-ja' */ '@formatjs/intl-pluralrules/dist/locale-data/ja'),
  ko: () => import(/* webpackChunkName: 'locale-data-ko' */ '@formatjs/intl-pluralrules/dist/locale-data/ko'),
  nl: () => import(/* webpackChunkName: 'locale-data-nl' */ '@formatjs/intl-pluralrules/dist/locale-data/nl'),
  pl: () => import(/* webpackChunkName: 'locale-data-pl' */ '@formatjs/intl-pluralrules/dist/locale-data/pl'),
  pt: () => import(/* webpackChunkName: 'locale-data-pt' */ '@formatjs/intl-pluralrules/dist/locale-data/pt'),
  ru: () => import(/* webpackChunkName: 'locale-data-ru' */ '@formatjs/intl-pluralrules/dist/locale-data/ru'),
  sv: () => import(/* webpackChunkName: 'locale-data-sv' */ '@formatjs/intl-pluralrules/dist/locale-data/sv'),
  th: () => import(/* webpackChunkName: 'locale-data-th' */ '@formatjs/intl-pluralrules/dist/locale-data/th'),
  tr: () => import(/* webpackChunkName: 'locale-data-tr' */ '@formatjs/intl-pluralrules/dist/locale-data/tr'),
  uk: () => import(/* webpackChunkName: 'locale-data-uk' */ '@formatjs/intl-pluralrules/dist/locale-data/uk'),
  vi: () => import(/* webpackChunkName: 'locale-data-vi' */ '@formatjs/intl-pluralrules/dist/locale-data/vi'),
  zh: () => import(/* webpackChunkName: 'locale-data-zh' */ '@formatjs/intl-pluralrules/dist/locale-data/zh'),
  nb: () => import(/* webpackChunkName: 'locale-data-nb' */ '@formatjs/intl-pluralrules/dist/locale-data/nb'),
};

const listFormatDataByCode = {
  cs: () => import(/* webpackChunkName: 'locale-data-cs' */ '@formatjs/intl-listformat/locale-data/cs'),
  da: () => import(/* webpackChunkName: 'locale-data-da' */ '@formatjs/intl-listformat/locale-data/da'),
  de: () => import(/* webpackChunkName: 'locale-data-de' */ '@formatjs/intl-listformat/locale-data/de'),
  en: () => import(/* webpackChunkName: 'locale-data-en' */ '@formatjs/intl-listformat/locale-data/en'),
  es: () => import(/* webpackChunkName: 'locale-data-es' */ '@formatjs/intl-listformat/locale-data/es'),
  fi: () => import(/* webpackChunkName: 'locale-data-fi' */ '@formatjs/intl-listformat/locale-data/fi'),
  fr: () => import(/* webpackChunkName: 'locale-data-fr' */ '@formatjs/intl-listformat/locale-data/fr'),
  hi: () => import(/* webpackChunkName: 'locale-data-hi' */ '@formatjs/intl-listformat/locale-data/hi'),
  id: () => import(/* webpackChunkName: 'locale-data-id' */ '@formatjs/intl-listformat/locale-data/id'),
  it: () => import(/* webpackChunkName: 'locale-data-it' */ '@formatjs/intl-listformat/locale-data/it'),
  ja: () => import(/* webpackChunkName: 'locale-data-ja' */ '@formatjs/intl-listformat/locale-data/ja'),
  ko: () => import(/* webpackChunkName: 'locale-data-ko' */ '@formatjs/intl-listformat/locale-data/ko'),
  nl: () => import(/* webpackChunkName: 'locale-data-nl' */ '@formatjs/intl-listformat/locale-data/nl'),
  pl: () => import(/* webpackChunkName: 'locale-data-pl' */ '@formatjs/intl-listformat/locale-data/pl'),
  pt: () => import(/* webpackChunkName: 'locale-data-pt' */ '@formatjs/intl-listformat/locale-data/pt'),
  ru: () => import(/* webpackChunkName: 'locale-data-ru' */ '@formatjs/intl-listformat/locale-data/ru'),
  sv: () => import(/* webpackChunkName: 'locale-data-sv' */ '@formatjs/intl-listformat/locale-data/sv'),
  th: () => import(/* webpackChunkName: 'locale-data-th' */ '@formatjs/intl-listformat/locale-data/th'),
  tr: () => import(/* webpackChunkName: 'locale-data-tr' */ '@formatjs/intl-listformat/locale-data/tr'),
  uk: () => import(/* webpackChunkName: 'locale-data-uk' */ '@formatjs/intl-listformat/locale-data/uk'),
  vi: () => import(/* webpackChunkName: 'locale-data-vi' */ '@formatjs/intl-listformat/locale-data/vi'),
  zh: () => import(/* webpackChunkName: 'locale-data-zh' */ '@formatjs/intl-listformat/locale-data/zh'),
  nb: () => import(/* webpackChunkName: 'locale-data-nb' */ '@formatjs/intl-listformat/locale-data/nb'),
};

const importTranslations = (locale) => {
  const promise =
    locale === 'en'
      ? import(/* webpackChunkName: 'translations-en' */ /* webpackMode: "eager" */ `../../assets/locale/texts_en.json`)
      : import(/* webpackChunkName: 'translations-[request]' */ `../../assets/locale/texts_${locale}.json`);
  return promise.then(getDefault, returnEmpty);
};

const LoadingComponent: FC = () => <FullHeightLoader className={styles.loader} />;

const IntlProvider = (props) => {
  const {
    mmgrSettings: { translations, isShowKeysExperimentEnabled },
  } = useMMGRSettings();
  const messages = isShowKeysExperimentEnabled
    ? // set messages to be keys
      mapObjIndexed((_value, key) => key, props.messages)
    : { ...props.messages, ...translations };

  return <ReactIntlProvider {...props} messages={messages} />;
};

const EnhancedIntlProvider = compose<any, Partial<IntlConfig>>(
  withExperiments,
  withProps(({ messages, experiments }) => ({
    messages: abTranslate(experiments.experiments, messages),
  })),
)(IntlProvider);

const debouncedErrorLogger = debounce((error) => {
  errorLogger.log(BI_ERRORS.general.failedTranslations, { reason: new Error(error), p1: locale });
}, 1000);

export const LocaleProvider: FC<IntlConfig> = (props) => {
  const { loading: areTranslationsLoading, value: translations } = useAsync(() =>
    window.__TRANSLATIONS__
      ? Promise.resolve(window.__TRANSLATIONS__)
      : Promise.all([
          importTranslations(locale).catch((err) => {
            const isNotSupportedLocale = err && err.message && err.message.includes('Cannot find module');
            if (!isNotSupportedLocale) {
              console.error(err);
            }
            return {};
          }),
          importTranslations('en'),
        ]).then(([currentTranslation, enTranslation]) => ({ ...enTranslation, ...currentTranslation })),
  );

  const { loading: isLocaleDataLoading } = useAsync(() =>
    Promise.all([pluralRulesDataByCode[correctLocale](), listFormatDataByCode[correctLocale]()]),
  );

  return any(Boolean, [areTranslationsLoading, isLocaleDataLoading]) ? (
    <LoadingComponent />
  ) : (
    <EnhancedIntlProvider
      messages={translations}
      locale={correctLocale}
      textComponent={Fragment}
      onError={debouncedErrorLogger}
      {...props}
    />
  );
};
